import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { Box, ListItemButton, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { TAAS_SANS } from "@/shared/branding/taasBrand";
import useThemeStore from "@/shared/stores/useThemeStore";
import { formatDeckCardTypeLabel } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import {
  formatTocReleaseLabel,
  getTocExpReleaseLabel,
  stripDeckCardHtml,
} from "@/cards/common/utils/app-deck/deckCardTocDisplay";
import { findDemoCardEmbeddedExps } from "@/cards/demo-card/utils/demoCardPreviewUtils";
import { findSeasonCardEmbeddedExps } from "@/cards/season-card/utils/seasonCardPreviewUtils";

export type CardTocPageItem = {
  pageIndex: number;
  title: string;
  description?: string;
  locked?: boolean;
  releaseLabel?: string | null;
  sessionMeta?: string;
};

type CardTocProps = {
  talentName?: string;
  pageCount: number;
  pages: CardTocPageItem[];
  currentPage: number;
  onSelectPage: (pageIndex: number) => void;
};

/**
 * Table of contents — presented like the index of a printed book: a quiet
 * eyebrow rule, serif folios, and dotted leaders running out to a release
 * status. No overview card, no chips — the list itself is the whole page.
 */
export function CardToc({ talentName, pages, currentPage, onSelectPage }: CardTocProps) {
  const { t } = useTranslation("taascard");
  const { darkMode } = useThemeStore();

  const titleColor = darkMode ? "#f7f5fa" : "#1a1528";
  const bodyColor = darkMode ? "rgba(247,245,250,0.68)" : "#655f70";
  const mutedColor = darkMode ? "rgba(247,245,250,0.5)" : "#8a8494";
  // const accentColor = darkMode ? "#c4b5fd" : "#7c3aed";
  const accentColor = "#B85335";
  const activeBg = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)";
  const hoverBg = darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";

  return (
    <Box
      sx={{
        px: 0.25,
        pb: 0,
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {pages.length > 0 ? (
        <Stack
          spacing={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Masthead */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              pb: 1.25,
              mb: 0.25,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: TAAS_SANS,
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: mutedColor,
                }}
              >
                {t("card_table_of_contents")}
              </Typography>
              {talentName?.trim() ? (
                <Typography
                  sx={{
                    mt: 0.65,
                    fontFamily: TAAS_SANS,
                    fontSize: "0.72rem",
                    color: bodyColor,
                  }}
                >
                  {talentName}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {/* Index */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              scrollbarColor: darkMode
                ? "color-mix(in srgb, var(--primary-600) 35%, transparent) transparent"
                : "var(--primary-300) transparent",
              py: 0.25,
            }}
          >
            <Stack spacing={0.15}>
              {pages.map((page, index) => {
                const isActive = currentPage === page.pageIndex;
                const isLocked = Boolean(page.locked);

                return (
                  <ListItemButton
                    key={`${page.pageIndex}-${page.title}`}
                    disabled={isLocked}
                    selected={isActive}
                    disableRipple
                    onClick={isLocked ? undefined : () => onSelectPage(page.pageIndex)}
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      py: 1.15,
                      px: 0.15,
                      alignItems: "flex-start",
                      gap: 1.25,
                      opacity: isLocked ? 0.5 : 1,
                      transition: "background-color 160ms ease, opacity 160ms ease",
                      "&::before": {
                        display: "none",
                      },
                      "&.Mui-disabled": { opacity: 0.5 },
                      "&.Mui-selected": {
                        bgcolor: activeBg,
                        "&:hover": { bgcolor: activeBg },
                      },
                      "&:hover": {
                        bgcolor: isLocked ? "transparent" : hoverBg,
                        "& .toc-chevron": {
                          transform: "translateX(2px)",
                          opacity: isLocked ? 0.4 : 1,
                        },
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 18,
                        pt: 0.15,
                        fontFamily: TAAS_SANS,
                        fontSize: "0.98rem",
                        fontWeight: 500,
                        color: accentColor,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1.3,
                        transition: "color 160ms ease",
                      }}
                    >
                      {index + 1}
                    </Typography>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.92rem",
                            fontWeight: 700,
                            color: titleColor,
                            fontFamily: TAAS_SANS,
                            lineHeight: 1.35,
                            flexShrink: 1,
                            minWidth: 0,
                          }}
                        >
                          {page.title}
                        </Typography>
                      </Box>

                      {page.description ? (
                        <Typography
                          sx={{
                            mt: 0.2,
                            fontSize: "0.74rem",
                            lineHeight: 1.4,
                            color: mutedColor,
                            fontFamily: TAAS_SANS,
                          }}
                        >
                          {page.description.trim().split(/\s+/).slice(0, 5).join(" ")}
                          {page.description.trim().split(/\s+/).length > 5 ? "…" : ""}
                        </Typography>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        pt: 0.3,
                      }}
                    >
                      {isLocked ? (
                        <LockRoundedIcon sx={{ color: mutedColor, fontSize: 15 }} />
                      ) : (
                        <ChevronRightRoundedIcon
                          className="toc-chevron"
                          sx={{
                            color: mutedColor,
                            fontSize: 20,
                            opacity: isActive ? 1 : 0.72,
                            transition: "transform 160ms ease, opacity 160ms ease",
                          }}
                        />
                      )}
                    </Box>
                  </ListItemButton>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}

const normalizeTocPageTypeKey = (type?: string) =>
  String(type ?? "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

const isDemoCardTocContext = (cardData?: any) => {
  const template = String(cardData?.template ?? "")
    .trim()
    .toLowerCase();
  if (template.includes("demo card")) return true;
  const typeKey = normalizeTocPageTypeKey(
    String(cardData?.type ?? cardData?.pages?.[0]?.type ?? "")
  );
  return typeKey === "democard";
};

const isSeasonCardTocContext = (cardData?: any) => {
  const template = String(cardData?.template ?? "")
    .trim()
    .toLowerCase();
  if (template.includes("season card")) return true;
  const typeKey = normalizeTocPageTypeKey(
    String(cardData?.type ?? cardData?.pages?.[0]?.type ?? "")
  );
  return typeKey === "seasoncard" || typeKey.startsWith("seasoncard");
};

const getExpSessionMeta = (exp: Record<string, unknown>): string | undefined => {
  const parts = [exp.session_type, exp.status].filter(Boolean).map(String);
  return parts.length > 0 ? parts.join(" · ") : undefined;
};

const buildExpTocItem = (
  exp: Record<string, unknown>,
  pageIndex: number,
  locked: boolean,
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string
): CardTocPageItem => {
  const title =
    String(exp.title || exp.theme_title || exp.name || "").trim() ||
    formatDeckCardTypeLabel("demo_card_exps") ||
    t("card_toc_page_number", {
      count: pageIndex,
    });
  const description = stripDeckCardHtml(
    String(exp.description || exp.summary || exp.caption || "")
  );
  const releaseLabel = getTocExpReleaseLabel(
    {
      release_date: exp.release_date != null ? String(exp.release_date) : undefined,
      start_time: exp.start_time != null ? String(exp.start_time) : undefined,
      title: typeof exp.title === "string" ? exp.title : undefined,
      theme_title: typeof exp.theme_title === "string" ? exp.theme_title : undefined,
      status: typeof exp.status === "string" ? exp.status : undefined,
      session_type: typeof exp.session_type === "string" ? exp.session_type : undefined,
    },
    t
  );

  return {
    pageIndex,
    title,
    description: description || undefined,
    locked,
    releaseLabel,
    sessionMeta: getExpSessionMeta(exp),
  };
};

const getPageContentTitle = (content: Record<string, unknown>): string => {
  const candidates = [
    content.title,
    content.songTitle,
    content.song_title,
    content.theme_title,
    content.name,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

/** Friendly TOC label when the page has no content title. */
const getTocTitleForPageType = (
  typeKey: string,
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string
): string => {
  if (typeKey === "audio") {
    return t("card_toc_audio");
  }
  if (typeKey === "endingimage") {
    return t("card_toc_ending_image");
  }
  // Single-card ending cover is injected as cover_image with no title.
  if (typeKey === "coverimage") {
    return t("card_toc_ending_image");
  }
  if (typeKey === "image") {
    return t("card_toc_image");
  }
  if (typeKey === "video") {
    return t("card_toc_video");
  }
  if (typeKey === "text") {
    return t("card_toc_text");
  }
  if (typeKey === "youtube") {
    return t("card_toc_youtube");
  }
  if (typeKey === "postcard" || typeKey === "post_card") {
    return t("card_toc_postcard");
  }
  if (typeKey === "news") {
    return t("card_toc_news");
  }
  if (typeKey === "about") {
    return t("card_toc_about");
  }
  if (typeKey === "bonus") {
    return t("card_toc_bonus");
  }
  if (typeKey === "instrument") {
    return t("card_toc_instrument");
  }
  if (typeKey === "cardtoc") {
    return t("card_table_of_contents");
  }
  return formatDeckCardTypeLabel(typeKey) || t("card_toc_page");
};

export function buildCardTocPages(options: {
  allPages: Array<{ type?: string; content?: any }>;
  cardData?: any;
  maxNavigablePage: number;
  includeCoverPage?: boolean;
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string;
}): CardTocPageItem[] {
  const { allPages, cardData, maxNavigablePage, includeCoverPage = false, t } = options;
  const coverOffset = includeCoverPage ? 1 : 0;
  const coverTitle = String("Card Description");
  const coverDescription = stripDeckCardHtml(
    String(cardData?.description || cardData?.content?.description || "")
  );

  const cover: CardTocPageItem = {
    pageIndex: 0,
    title: coverTitle,
    description: coverDescription || undefined,
    locked: 0 > maxNavigablePage,
  };

  const contentPages: CardTocPageItem[] = allPages.map((page, index) => {
    const pageIndex = index + coverOffset;
    const content = (page?.content ?? {}) as Record<string, unknown>;
    const typeKey = normalizeTocPageTypeKey(page?.type);
    const locked = pageIndex > maxNavigablePage;

    // Talent "add experience" pages copy the whole card into content, so
    // content.title becomes "username's My Cards" — use a clear TOC label instead.
    const isExpCreationPage =
      typeKey.endsWith("expcreation") ||
      typeKey === "democardnormalexps" ||
      typeKey === "seasoncardnormalexps";

    if (isExpCreationPage) {
      return {
        pageIndex,
        title: t("card_toc_add_more_page"),
        locked,
      };
    }

    if (typeKey === "cardtoc") {
      return {
        pageIndex,
        title: t("card_table_of_contents"),
        locked,
      };
    }

    const contentTitle = getPageContentTitle(content);
    const title = contentTitle || getTocTitleForPageType(typeKey, t);
    const description = stripDeckCardHtml(
      String(content?.description || content?.summary || content?.caption || "")
    );

    if (typeKey === "democardexps") {
      return buildExpTocItem(content, pageIndex, locked, t);
    }

    if (typeKey === "seasoncardexps") {
      return buildExpTocItem(content, pageIndex, locked, t);
    }

    if (typeKey === "democardexplist") {
      const episodes = Array.isArray(content?.allEpisodes) ? content.allEpisodes : [];
      return {
        pageIndex,
        title:
          contentTitle ||
          formatDeckCardTypeLabel("demo_card_exp_list") ||
          t("card_toc_episode_list"),
        description:
          description ||
          (episodes.length > 0
            ? t("card_toc_overview_body", {
                count: episodes.length,
              })
            : undefined),
        locked,
      };
    }

    if (typeKey === "seasoncardexplist") {
      const series = Array.isArray(content?.allEpisodes) ? content.allEpisodes : [];
      return {
        pageIndex,
        title:
          contentTitle ||
          formatDeckCardTypeLabel("season_card_exp_list") ||
          t("card_toc_series_list"),
        description:
          description ||
          (series.length > 0
            ? t("card_toc_overview_body", {
                count: series.length,
              })
            : undefined),
        locked,
      };
    }

    return {
      pageIndex,
      title,
      description: description || undefined,
      locked,
      releaseLabel: formatTocReleaseLabel(
        (content?.release_date as string | null | undefined) ||
          (content?.start_time as string | null | undefined) ||
          null,
        t
      ),
    };
  });

  // Unpurchased: blend API table_of_contents into the TOC as locked rows.
  // Keeps real book pages, then adds any TOC titles that are not already listed
  // (needed for single cards that already have pages in the book).
  const isPurchased = cardData?.purchase != null;
  if (!isPurchased) {
    const apiToc = Array.isArray(cardData?.table_of_contents)
      ? (cardData.table_of_contents as Array<{
          type?: string;
          title?: string;
          description?: string;
        }>)
      : [];

    if (apiToc.length > 0) {
      const existingTitles = new Set(
        contentPages
          .map((page) =>
            String(page.title ?? "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      );

      let nextIndex = contentPages.length + coverOffset;
      for (const item of apiToc) {
        const typeKey = normalizeTocPageTypeKey(item?.type);
        const title =
          String(item?.title ?? "").trim() ||
          getTocTitleForPageType(typeKey, t) ||
          t("card_toc_page");
        const titleKey = title.toLowerCase();
        // Skip duplicates already shown from real book pages.
        if (existingTitles.has(titleKey)) continue;

        const description = stripDeckCardHtml(String(item?.description ?? ""));
        contentPages.push({
          pageIndex: nextIndex,
          title,
          description: description || undefined,
          locked: true,
        });
        existingTitles.add(titleKey);
        nextIndex += 1;
      }
    }
  }

  // Unpurchased demo: episode list + episodes stay in TOC (locked), not as book pages.
  const hasDemoEpisodePages = allPages.some((page) => {
    const key = normalizeTocPageTypeKey(page?.type);
    return key === "democardexps" || key === "democardexplist";
  });
  if (isDemoCardTocContext(cardData) && !isPurchased && !hasDemoEpisodePages) {
    const allExps = findDemoCardEmbeddedExps(cardData);
    if (allExps.length > 0) {
      let nextIndex = contentPages.length + coverOffset;
      contentPages.push({
        pageIndex: nextIndex,
        title: formatDeckCardTypeLabel("demo_card_exp_list") || t("card_toc_episode_list"),
        description: t("card_toc_overview_body", {
          count: allExps.length,
        }),
        locked: true,
      });
      nextIndex += 1;

      for (const exp of allExps) {
        contentPages.push(buildExpTocItem(exp, nextIndex, true, t));
        nextIndex += 1;
      }
    }
  }

  // Unpurchased season: series list + series stay in TOC (locked), same as demo.
  const hasSeasonExpPages = allPages.some((page) => {
    const key = normalizeTocPageTypeKey(page?.type);
    return key === "seasoncardexps" || key === "seasoncardexplist";
  });
  if (isSeasonCardTocContext(cardData) && !isPurchased && !hasSeasonExpPages) {
    const allExps = findSeasonCardEmbeddedExps(cardData);
    if (allExps.length > 0) {
      let nextIndex = contentPages.length + coverOffset;
      contentPages.push({
        pageIndex: nextIndex,
        title: formatDeckCardTypeLabel("season_card_exp_list") || t("card_toc_series_list"),
        description: t("card_toc_overview_body", {
          count: allExps.length,
        }),
        locked: true,
      });
      nextIndex += 1;

      for (const exp of allExps) {
        contentPages.push(buildExpTocItem(exp, nextIndex, true, t));
        nextIndex += 1;
      }
    }
  }

  return includeCoverPage ? [cover, ...contentPages] : contentPages;
}
